import { Model, Toast } from 'jamespot-front-business';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import { JRCInputTinyMCERawProps } from 'jamespot-react-components';
import { AIModal } from './extension/AIModal';
import { MarkdownModal } from './extension/MarkdownModal';
import { useMarkdownModal } from '../../hooks/useMarkdownModal';
import { commonComponentsRegistry } from '../../registry/CommonRegistry';
import { tinymceCommonOptions } from '../../initTinymce';
import { getTinyMCEMentionsQueries } from '../../utils/tinymce';
import { useAI } from '../../hooks/useAI';
import { useAppDispatch, useAppSelector } from '../../redux/store';

const JRCInputTinyMCERaw = commonComponentsRegistry.getLazyComponent('InputTinyMCERaw');

export type JRCInputTinyMCEWithExtProps = Omit<
    JRCInputTinyMCERawProps,
    'commonOptions' | 'mentionsQueries' | 'additionalExtensions'
>;

export const JRCInputTinyMCEWithExtRaw = (props: JRCInputTinyMCEWithExtProps) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const models = useAppSelector(Model.selectors.selectAll);
    const { query, ext: extAI, isActive: isAIActive } = useAI();
    const {
        open,
        onInsert,
        close: closeMarkdownModal,
        ext: extMarkdown,
        isActive: isMarkdownActive,
    } = useMarkdownModal();

    const extArray = useMemo(() => {
        return [...(isMarkdownActive ? [extMarkdown] : []), ...(isAIActive ? [extAI] : [])];
    }, [extAI, extMarkdown, isAIActive, isMarkdownActive]);

    const onMentionsQueryError = useCallback(
        (message?: string) => {
            dispatch(Toast.actions.error({ label: message ?? intl.formatMessage({ id: 'GLOBAL_Error' }) }));
        },
        [dispatch, intl],
    );

    const tinyMentionsQueries = useMemo(() => {
        return getTinyMCEMentionsQueries(models, theme, onMentionsQueryError);
    }, [models, onMentionsQueryError, theme]);

    const commonOptions = useMemo(() => tinymceCommonOptions, []);

    return (
        <>
            {commonOptions && (
                <JRCInputTinyMCERaw
                    commonOptions={commonOptions}
                    additionalExtensions={extArray}
                    mentionsQueries={tinyMentionsQueries}
                    {...props}
                />
            )}
            {isAIActive && <AIModal query={query} />}
            {isMarkdownActive && <MarkdownModal onInsert={onInsert} open={open} close={closeMarkdownModal} />}
        </>
    );
};
